/* ════════════════════════════════════════════════════════════
   premium-ux.js — Luxury motion system for Babuco
   Requires: Lenis 1.1.13, GSAP 3.12.5, ScrollTrigger, SplitText
   ════════════════════════════════════════════════════════════ */
'use strict';

(function () {

  /* ─── Feature detection ──────────────────────────────────── */
  var REDUCED    = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var IS_SHOPIFY = typeof Shopify !== 'undefined';

  /* ─── Boot ───────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    // Libraries are defer-loaded in document order, so they're
    // available by the time DOMContentLoaded fires.
    if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
      console.warn('[PremiumUX] Required libraries not loaded.');
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    if (typeof SplitText !== 'undefined') gsap.registerPlugin(SplitText);

    var lenis = initLenis();
    initScrollProgress(lenis);
    initPageTransitions();
    initHeaderScroll();
    if (!REDUCED) {
      initHeroEntry();
      initScrollReveals();
      initParallax();
    }
    initNavAnimations();
    initATCMicroInteraction();
    initMarquee();
    initAnnouncementMarquee();
    initProductCardReveal();
    initCollectionBannerParallax();
    initProductDescAnchors();
    initPriceMicroType();
    initFilterTransitions();
    initFilterDesktopFix();
    initPriceSliders();
    initFilterSwipeClose();
    initLoadMore();
    initInfiniteScroll();
    initBackToTop();
    initColorSwatches();
    initMaterialAccordion();
    initSizeGuideDrawer();
    initGallery();
    initDisclosureScroll();

    // Resize Lenis after all images/fonts load so scroll limit is accurate
    window.addEventListener('load', function () {
      if (window.__lenis) window.__lenis.resize();
    });
  });

  /* ════════════════════════════════════════════════════════════
     1. LENIS SMOOTH SCROLL
     ════════════════════════════════════════════════════════════ */
  function initLenis() {
    var lenis = new Lenis({
      lerp: 0.08,
      duration: 1.4,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 1.0,
    });

    window.__lenis = lenis;

    // Connect Lenis RAF to GSAP ticker
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    // Keep ScrollTrigger in sync with Lenis scroll position
    lenis.on('scroll', ScrollTrigger.update);

    return lenis;
  }

  /* ════════════════════════════════════════════════════════════
     3. PAGE TRANSITION OVERLAY
     ════════════════════════════════════════════════════════════ */
  function initPageTransitions() {
    var overlay = document.createElement('div');
    overlay.id  = 'page-overlay';
    document.body.appendChild(overlay);

    // Page-in: animate overlay off-screen downward
    gsap.fromTo(overlay,
      { yPercent: 0 },
      {
        yPercent: 100,
        duration: 0.42,
        ease: 'power2.out',
        delay: 0.05,
        onStart: function () {
          if (window.__lenis) window.__lenis.start();
        },
      }
    );

    // Intercept internal navigation
    document.addEventListener('click', function (e) {
      var anchor = e.target.closest('a');
      if (!anchor || !anchor.href) return;

      var url;
      try { url = new URL(anchor.href, window.location.origin); }
      catch (err) { return; }

      // Guard clauses — skip transition
      if (url.hostname !== window.location.hostname) return;
      var attrHref = anchor.getAttribute('href') || '';
      if (attrHref.charAt(0) === '#' || attrHref.indexOf('#') === 0) return;
      if (anchor.target === '_blank') return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (url.href === window.location.href) return;
      if (url.pathname.indexOf('/checkout') === 0) return;
      if (url.pathname === '/cart') return;
      if (anchor.closest('form')) return;  // skip if inside a form

      e.preventDefault();

      if (window.__lenis) window.__lenis.stop();

      gsap.fromTo(overlay,
        { yPercent: -100 },
        {
          yPercent: 0,
          duration: 0.42,
          ease: 'power2.in',
          onComplete: function () { window.location.href = anchor.href; },
        }
      );
    });

    // Handle browser back/forward (bfcache)
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        gsap.set(overlay, { yPercent: 0 });
        gsap.to(overlay, { yPercent: 100, duration: 0.42, ease: 'power2.out', delay: 0.1 });
        if (window.__lenis) window.__lenis.start();
      }
    });
  }

  /* ════════════════════════════════════════════════════════════
     4. SCROLL PROGRESS BAR
     ════════════════════════════════════════════════════════════ */
  function initScrollProgress(lenis) {
    var bar = document.createElement('div');
    bar.id  = 'scroll-progress';
    document.body.appendChild(bar);

    // Only show on specific templates
    var bodyClass = document.body.className;
    var showOn = bodyClass.indexOf('template-product') !== -1 ||
                 bodyClass.indexOf('template-article') !== -1 ||
                 bodyClass.indexOf('template-page')    !== -1;
    if (!showOn) return;

    lenis.on('scroll', function (e) {
      var progress = Math.min(e.scroll / e.limit, 1);
      bar.style.transform = 'scaleX(' + progress + ')';
      if (progress > 0.05 && progress < 0.98) {
        bar.classList.add('is-visible');
      } else {
        bar.classList.remove('is-visible');
      }
    });
  }

  /* ════════════════════════════════════════════════════════════
     5. STICKY HEADER TRANSFORM ON SCROLL
     ════════════════════════════════════════════════════════════ */
  function initHeaderScroll() {
    var headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;

    var ticking = false;

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          headerWrapper.classList.toggle('header--scrolled', window.scrollY > 80);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Set --header-height CSS variable so sticky toolbar can offset correctly
    function updateHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', headerWrapper.offsetHeight + 'px');
    }
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight, { passive: true });
    window.addEventListener('orientationchange', function () {
      // Re-measure after the browser has finished re-laying out the page
      setTimeout(updateHeaderHeight, 200);
    }, { passive: true });

    // Sticky toolbar shadow on scroll
    var toolbar = document.querySelector('.heritage-toolbar');
    if (toolbar) {
      window.addEventListener('scroll', function () {
        toolbar.classList.toggle('is-stuck', window.scrollY > 10);
      }, { passive: true });
    }
  }

  /* ════════════════════════════════════════════════════════════
     6. HERO ENTRY ANIMATION
     ════════════════════════════════════════════════════════════ */
  function initHeroEntry() {
    var hero = document.querySelector('.banner');
    if (!hero) return;

    var heroMedia   = hero.querySelector('.banner__media');
    var heroHeading = hero.querySelector('.banner__heading');
    var heroText    = hero.querySelector('.banner__text');
    var heroButtons = hero.querySelector('.banner__buttons');
    var navItems    = document.querySelectorAll('.header__inline-menu .header__menu-item');

    var tl = gsap.timeline({ delay: 0.5 }); // wait for overlay to exit

    // a. Hero media — no initial-hide animation: any opacity/clip-path set here
    //    creates a white flash on transparent-header pages because the page
    //    background shows through for the delay period. The image appears
    //    naturally; parallax (initParallax) already gives it depth motion.

    // b. Hero heading — SplitText words or fallback
    if (heroHeading) {
      if (typeof SplitText !== 'undefined') {
        var split = new SplitText(heroHeading, { type: 'words' });
        gsap.set(split.words, { y: 60, opacity: 0 });
        tl.to(split.words, {
          y: 0, opacity: 1,
          duration: 0.9, ease: 'power3.out', stagger: 0.07,
        }, 0.15);
      } else {
        gsap.set(heroHeading, { y: 40, opacity: 0 });
        tl.to(heroHeading, { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out' }, 0.15);
      }
    }

    // c. Subtext + CTA
    var fadeEls = [heroText, heroButtons].filter(Boolean);
    if (fadeEls.length) {
      gsap.set(fadeEls, { y: 20, opacity: 0 });
      tl.to(fadeEls, { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out', stagger: 0.1 }, '-=0.3');
    }

    // d. Nav items — fade in simultaneously with image start
    if (navItems.length) {
      gsap.set(navItems, { opacity: 0 });
      tl.to(navItems, { opacity: 1, duration: 0.5, ease: 'power2.out', stagger: 0.04 }, 0);
    }
  }

  /* ════════════════════════════════════════════════════════════
     7. SCROLL-TRIGGERED REVEALS
     ════════════════════════════════════════════════════════════ */
  function initScrollReveals() {

    // ─ Product cards (batch for stagger by row)
    var cards = gsap.utils.toArray('.card-wrapper.product-card-wrapper');
    if (cards.length) {
      gsap.set(cards, { opacity: 0, y: 40 });
      ScrollTrigger.batch(cards, {
        start: 'top 88%',
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1, y: 0,
            duration: 0.85, ease: 'power3.out',
            stagger: { each: 0.1, from: 'start' },
            overwrite: true,
          });
        },
        once: true,
      });
    }

    // ─ Section headings — SplitText lines with overflow:hidden
    var headings = gsap.utils.toArray('h2, h3');
    headings.forEach(function (heading) {
      // Skip: inside hero banner (already animated)
      if (heading.closest('.banner')) return;
      // Skip: inside header
      if (heading.closest('.header')) return;
      // Skip: product card titles — handled by initProductCardReveal, which
      // has its own timing synced to the card/price reveal. Letting this
      // generic reveal also grab them fights over the same element and can
      // leave the title's opacity stuck at 0 permanently.
      if (heading.closest('.product-card')) return;

      if (typeof SplitText !== 'undefined') {
        var parentOverflows = [];
        var split = new SplitText(heading, {
          type: 'lines',
          linesClass: 'split-line',
        });
        // Wrap each line parent in overflow:hidden
        split.lines.forEach(function (line) {
          if (!line.closest('[style*="overflow"]')) {
            line.parentElement.style.overflow = 'hidden';
            parentOverflows.push(line.parentElement);
          }
        });
        gsap.set(split.lines, { y: '105%' });

        ScrollTrigger.create({
          trigger: heading,
          start: 'top 88%',
          once: true,
          onEnter: function () {
            gsap.to(split.lines, {
              y: '0%', duration: 0.9, ease: 'power3.out', stagger: 0.06,
              onComplete: function () {
                // Clean up overflow restriction after animation
                parentOverflows.forEach(function (el) {
                  el.style.overflow = '';
                });
              },
            });
          },
        });
      } else {
        // Fallback: simple fade + translate
        gsap.set(heading, { opacity: 0, y: 30 });
        ScrollTrigger.create({
          trigger: heading,
          start: 'top 88%',
          once: true,
          onEnter: function () {
            gsap.to(heading, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' });
          },
        });
      }
    });

    // ─ Large editorial images (not product gallery thumbnails)
    var editorialImgs = gsap.utils.toArray('.banner__media');
    editorialImgs.forEach(function (el) {
      var img = el.querySelector('img');
      if (!img) return;
      // Skip ambient-animated banners (Shopify's built-in parallax)
      if (el.closest('.animate--ambient')) return;

      gsap.set(img, { scale: 1.08 });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(img, { scale: 1, duration: 1.2, ease: 'power3.out' });
        },
      });
    });

    // ─ Feature sections: look for common two-column layouts
    var richTextItems = gsap.utils.toArray('.rich-text__blocks, .content-with-text__media');
    richTextItems.forEach(function (el, i) {
      var fromX = i % 2 === 0 ? -30 : 30;
      gsap.set(el, { opacity: 0, x: fromX });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(el, { opacity: 1, x: 0, duration: 0.85, ease: 'power3.out' });
        },
      });
    });

    // ─ Generic sections: testimonials, footer items, promo blocks
    var genericReveal = gsap.utils.toArray(
      '.testimonials__item, .footer__content-top > *, .promotions__item'
    );
    genericReveal.forEach(function (el) {
      gsap.set(el, { opacity: 0, y: 40 });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(el, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' });
        },
      });
    });

    // Refresh after setup so positions are correct
    ScrollTrigger.refresh();
  }

  /* ════════════════════════════════════════════════════════════
     8. PARALLAX ON EDITORIAL IMAGES
     ════════════════════════════════════════════════════════════ */
  function initParallax() {
    var banners = gsap.utils.toArray('.banner');
    banners.forEach(function (banner) {
      var mediaEl = banner.querySelector('.banner__media');
      var img     = mediaEl && mediaEl.querySelector('img');
      if (!img) return;
      // Skip Shopify's own ambient parallax
      if (mediaEl.classList.contains('animate--ambient')) return;

      // fromTo(-4 to 4) centres the img at mid-scroll so the top of
      // .banner__media is never exposed (fixes white bar on transparent-
      // header pages: at scrollY=0 the trigger is ~50% through, so a
      // plain gsap.to(yPercent:8) would shift the image down ~4% at load)
      gsap.fromTo(img,
        { yPercent: -4 },
        {
          yPercent: 4,
          ease: 'none',
          scrollTrigger: {
            trigger: banner,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.5,
          },
        }
      );
    });
  }

  /* ════════════════════════════════════════════════════════════
     9–10. PRODUCT CARD HOVER + NAV ANIMATIONS
     (Mostly handled in premium-ux.css — JS adds nav indicator)
     ════════════════════════════════════════════════════════════ */
  function initNavAnimations() {

    // ─ Sliding underline indicator for desktop nav
    var navContainer = document.querySelector('.header__inline-menu');
    var navLinks     = navContainer
      ? navContainer.querySelectorAll('.list-menu--inline > li > .header__menu-item')
      : [];

    if (navContainer && navLinks.length) {
      var indicator = document.createElement('div');
      indicator.className = 'nav-indicator';
      navContainer.appendChild(indicator);

      navLinks.forEach(function (link) {
        link.addEventListener('mouseenter', function () {
          var rect    = link.getBoundingClientRect();
          var navRect = navContainer.getBoundingClientRect();
          gsap.to(indicator, {
            x: rect.left - navRect.left,
            width: rect.width,
            opacity: 1,
            duration: 0.2,
            ease: 'power2.out',
          });
        });

        link.addEventListener('mouseleave', function () {
          gsap.to(indicator, { opacity: 0, duration: 0.15 });
        });
      });
    }

    // ─ Mobile menu: stagger items when drawer opens
    var drawerDetails = document.querySelector('#Details-menu-drawer-container');
    var drawerItems   = document.querySelectorAll('.menu-drawer__menu > li > .menu-drawer__menu-item');

    if (drawerDetails && drawerItems.length) {
      var menuObserver = new MutationObserver(function () {
        if (drawerDetails.hasAttribute('open')) {
          gsap.fromTo(drawerItems,
            { x: 20, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.45, ease: 'power3.out', stagger: 0.06, delay: 0.15 }
          );
        }
      });
      menuObserver.observe(drawerDetails, { attributes: true, attributeFilter: ['open'] });
    }

    // ─ Mobile menu background overlay
    var mobileOverlay = document.createElement('div');
    mobileOverlay.id  = 'mobile-menu-overlay';
    document.body.appendChild(mobileOverlay);

    if (drawerDetails) {
      var overlayObserver = new MutationObserver(function () {
        mobileOverlay.classList.toggle('is-visible', drawerDetails.hasAttribute('open'));
      });
      overlayObserver.observe(drawerDetails, { attributes: true, attributeFilter: ['open'] });

      mobileOverlay.addEventListener('click', function () {
        // Close the drawer by removing open attribute
        drawerDetails.removeAttribute('open');
      });
    }
  }

  /* ════════════════════════════════════════════════════════════
     11. ADD TO CART MICRO-INTERACTION
     ════════════════════════════════════════════════════════════ */
  function initATCMicroInteraction() {

    var cartIcon = document.querySelector('.header__icon--cart');

    // Bounce cart icon on any cart update (Shopify fires this custom event)
    document.addEventListener('cart:updated', function () {
      if (!cartIcon) return;
      gsap.fromTo(cartIcon,
        { scale: 1 },
        { scale: 1.25, duration: 0.2, ease: 'power2.out', yoyo: true, repeat: 1 }
      );

      // Animate count badge if present
      var badge = cartIcon.querySelector('.cart-count-bubble span[aria-hidden]');
      if (badge) {
        gsap.fromTo(badge,
          { y: -6, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.3, ease: 'power2.out' }
        );
      }
    });

    // Product page single-variant direct ATC button
    var atcForms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    atcForms.forEach(function (form) {
      var btn = form.querySelector('[name="add"]');
      if (!btn) return;

      var originalWidth    = null;
      var originalRadius   = null;
      var isAnimating      = false;

      form.addEventListener('submit', function () {
        if (isAnimating) return;
        isAnimating = true;

        originalWidth  = btn.offsetWidth;
        originalRadius = parseFloat(getComputedStyle(btn).borderRadius) || 0;

        btn.style.width    = originalWidth + 'px';
        btn.style.minWidth = 'unset';
        btn.classList.add('atc-loading');

        gsap.to(btn, { width: 44, borderRadius: 22, duration: 0.3, ease: 'power2.out' });
      });

      // Listen for Shopify's section:load or cart:add events as success signal
      document.addEventListener('cart:add', function onCartAdd() {
        if (!isAnimating) return;

        btn.classList.remove('atc-loading');
        btn.classList.add('atc-success');

        // After brief success moment, restore
        setTimeout(function () {
          btn.classList.remove('atc-success');
          gsap.to(btn, {
            width: originalWidth,
            borderRadius: originalRadius,
            duration: 0.3,
            ease: 'power2.inOut',
            onComplete: function () {
              btn.style.width    = '';
              btn.style.minWidth = '';
              isAnimating = false;
            },
          });
        }, 1800);

        document.removeEventListener('cart:add', onCartAdd);
      });

      // Safety fallback: restore after 4s
      setTimeout(function () {
        if (isAnimating) {
          btn.classList.remove('atc-loading', 'atc-success');
          gsap.to(btn, {
            width: originalWidth || '',
            borderRadius: originalRadius || 0,
            duration: 0.3,
            onComplete: function () {
              btn.style.width    = '';
              btn.style.minWidth = '';
              isAnimating = false;
            },
          });
        }
      }, 4000);
    });
  }

  /* ════════════════════════════════════════════════════════════
     12. HORIZONTAL MARQUEE
     ════════════════════════════════════════════════════════════ */
  function initMarquee() {
    var marquees = document.querySelectorAll('[data-marquee], .marquee');
    if (!marquees.length) return;

    marquees.forEach(function (section) {
      // Find or create the inner track
      var inner = section.querySelector('[data-marquee-inner], .marquee-inner');
      if (!inner) {
        inner = section.firstElementChild;
      }
      if (!inner) return;

      var original = inner.innerHTML;
      inner.innerHTML = original + original + original;
      inner.classList.add('marquee-track');

      var tween = gsap.to(inner, {
        x: '-33.333%',
        duration: 22,
        ease: 'none',
        repeat: -1,
      });

      section.addEventListener('mouseenter', function () { tween.timeScale(0.5); });
      section.addEventListener('mouseleave', function () { tween.timeScale(1); });
    });
  }

  /* ════════════════════════════════════════════════════════════
     13. FILTER / PLP RE-RENDER TRANSITIONS
     ════════════════════════════════════════════════════════════ */
  function initFilterTransitions() {
    var grid = document.querySelector('#product-grid');
    if (!grid) return;

    var isAnimating = false;

    var observer = new MutationObserver(function (mutations) {
      var added = [];
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList.contains('grid__item')) {
            added.push(node);
          }
        });
      });

      if (!added.length || isAnimating) return;
      isAnimating = true;

      gsap.fromTo(added,
        { opacity: 0, y: 20 },
        {
          opacity: 1, y: 0,
          duration: 0.5, ease: 'power3.out',
          stagger: 0.06,
          onComplete: function () {
            isAnimating = false;
            // Recalculate Lenis scroll height after DOM change
            if (window.__lenis) window.__lenis.resize();
          },
        }
      );
    });

    observer.observe(grid, { childList: true });
  }

  /* ════════════════════════════════════════════════════════════
     15. DISCLOSURE (CURRENCY / COUNTRY) POPUP — SCROLL PASSTHROUGH
     Lenis captures all wheel events. When the mouse is over a
     .disclosure__list, wheel events must still scroll the page.
     For lists that are themselves scrollable we chain to Lenis
     only at the scroll boundaries; otherwise we always forward.
     ════════════════════════════════════════════════════════════ */
  function initDisclosureScroll() {
    function attachToList(list) {
      if (list._disclosureScrollBound) return;
      list._disclosureScrollBound = true;

      list.addEventListener('wheel', function (e) {
        var isScrollable = list.scrollHeight > list.clientHeight + 1;
        var atTop        = list.scrollTop <= 0;
        var atBottom     = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

        /* Always forward when non-scrollable; at boundaries when scrollable */
        if (!isScrollable || (e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault();
          e.stopPropagation();
          var delta = e.deltaY || e.deltaX;
          if (window.__lenis) {
            window.__lenis.scrollTo(window.__lenis.scroll + delta, { lerp: 0.08 });
          } else {
            window.scrollBy(0, delta);
          }
        }
      }, { passive: false });
    }

    /* Attach to any lists already in the DOM */
    document.querySelectorAll('.disclosure__list').forEach(attachToList);

    /* Also attach when Shopify dynamically injects disclosure panels */
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('disclosure__list')) {
            attachToList(node);
          }
          node.querySelectorAll && node.querySelectorAll('.disclosure__list').forEach(attachToList);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ════════════════════════════════════════════════════════════
     14. PRODUCT IMAGE GALLERY CROSSFADE
     ════════════════════════════════════════════════════════════ */
  function initGallery() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    var mediaList = gallery.querySelector('.product__media-list');
    if (!mediaList) return;

    var slides = mediaList.querySelectorAll('.product__media-item');

    // Watch each slide for is-active class changes
    var classObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'class') return;
        var target = m.target;
        if (target.classList.contains('is-active')) {
          gsap.fromTo(target,
            { opacity: 0 },
            { opacity: 1, duration: 0.35, ease: 'power2.out', overwrite: true }
          );
        }
      });
    });

    slides.forEach(function (slide) {
      classObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    // Thumbnail hover: scale active indicator (CSS handles outline, JS adds scale)
    var thumbnails = document.querySelectorAll('.product-media-container');
    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('mouseenter', function () {
        gsap.to(thumb, { scale: 1.02, duration: 0.2, ease: 'power2.out' });
      });
      thumb.addEventListener('mouseleave', function () {
        gsap.to(thumb, { scale: 1, duration: 0.2, ease: 'power2.out' });
      });
    });
  }

  /* ════════════════════════════════════════════════════════════
     initPriceSliders — native dual <input type="range"> slider
     The track background is a linear-gradient driven by CSS variables
     --lo/--hi set on the .pf-slider container. No separate fill div,
     so there is nothing for the browser track pseudo-element to cover.
     ════════════════════════════════════════════════════════════ */
  function initPriceSliders() {
    /* Guard: only wire up elements that aren't already initialised.
       After AJAX re-renders fresh DOM nodes appear without data-pf-inited. */
    document.querySelectorAll('[data-price-ui]:not([data-pf-inited])').forEach(function (ui) {
      ui.dataset.pfInited = '1';

      var maxVal   = parseFloat(ui.dataset.rangeMax) || 0;
      var gteInput = document.getElementById(ui.dataset.gteId);
      var lteInput = document.getElementById(ui.dataset.lteId);
      if (!gteInput || !lteInput || maxVal === 0) return;

      var slider   = ui.querySelector('[data-price-slider]');
      var inputMin = ui.querySelector('.pf-slider__input--min');
      var inputMax = ui.querySelector('.pf-slider__input--max');
      var numMin   = ui.querySelector('.pf-price__num--min');
      var numMax   = ui.querySelector('.pf-price__num--max');
      if (!slider || !inputMin || !inputMax || !numMin || !numMax) return;

      function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

      /* ── Update the CSS-variable gradient on the track ── */
      function render() {
        var lo = parseFloat(inputMin.value) || 0;
        var hi = parseFloat(inputMax.value) || maxVal;
        var pLo = (lo / maxVal * 100).toFixed(2) + '%';
        var pHi = (hi / maxVal * 100).toFixed(2) + '%';
        slider.style.setProperty('--lo', pLo);
        slider.style.setProperty('--hi', pHi);
        numMin.value = Math.round(lo);
        numMax.value = Math.round(hi);
        /* Raise min thumb z-index when it's near the right edge so it
           stays reachable even when both thumbs are at the same position. */
        if (lo / maxVal > 0.5) {
          inputMin.classList.add('is-top');
        } else {
          inputMin.classList.remove('is-top');
        }
      }

      /* ── Push values to Shopify's hidden inputs + trigger AJAX refetch ── */
      function commit() {
        var lo = parseFloat(inputMin.value) || 0;
        var hi = parseFloat(inputMax.value) || maxVal;
        gteInput.value = (lo <= 0)      ? '' : String(Math.round(lo));
        lteInput.value = (hi >= maxVal) ? '' : String(Math.round(hi));
        gteInput.dispatchEvent(new Event('change', { bubbles: true }));
        lteInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* ── Range inputs → track gradient (live as the thumb moves) ── */
      inputMin.addEventListener('input', function () {
        /* Prevent min from crossing max */
        var hi = parseFloat(inputMax.value) || maxVal;
        if (parseFloat(this.value) > hi) this.value = hi;
        render();
      });
      inputMax.addEventListener('input', function () {
        /* Prevent max from crossing min */
        var lo = parseFloat(inputMin.value) || 0;
        if (parseFloat(this.value) < lo) this.value = lo;
        render();
      });

      /* ── Commit on mouseup / touchend (after dragging stops) ── */
      inputMin.addEventListener('change', function () { render(); commit(); });
      inputMax.addEventListener('change', function () { render(); commit(); });

      /* ── Number inputs → range inputs (live while typing) ── */
      numMin.addEventListener('input', function () {
        var v = clamp(parseFloat(this.value) || 0, 0, parseFloat(inputMax.value) || maxVal);
        inputMin.value = v;
        render();
      });
      numMax.addEventListener('input', function () {
        var v = clamp(parseFloat(this.value) || maxVal, parseFloat(inputMin.value) || 0, maxVal);
        inputMax.value = v;
        render();
      });
      numMin.addEventListener('change', function () { render(); commit(); });
      numMax.addEventListener('change', function () { render(); commit(); });

      /* ── Flush hidden inputs before form submits ── */
      var form = ui.closest('form');
      if (form) {
        form.addEventListener('submit', function () {
          var lo = parseFloat(inputMin.value) || 0;
          var hi = parseFloat(inputMax.value) || maxVal;
          gteInput.value = (lo <= 0)      ? '' : String(Math.round(lo));
          lteInput.value = (hi >= maxVal) ? '' : String(Math.round(hi));
        }, true);
      }

      /* ── Initial render ── */
      render();
    });

    /* ── Re-init after AJAX filter updates replace the filter HTML ──────────
       facets.js replaces .js-filter innerHTML; new [data-price-ui] nodes
       have no data-pf-inited so initPriceSliders picks them up next call. */
    if (!window.__pfSliderObserving) {
      window.__pfSliderObserving = true;
      var fw = document.getElementById('FacetsWrapperDesktop');
      if (fw) {
        var pfReinitTimer;
        new MutationObserver(function () {
          clearTimeout(pfReinitTimer);
          pfReinitTimer = setTimeout(initPriceSliders, 60);
        }).observe(fw, { childList: true, subtree: true });
      }
    }
  }

  /* ════════════════════════════════════════════════════════════
     FILTER DESKTOP FIX
     The tint overlay is body.filter-modal-open::before (CSS).
     We just need to:
       1. Add/remove body.filter-modal-open on desktop (facets.js
          only does this on mobile via lockScroll).
       2. Handle click-outside to close the panel.
     ════════════════════════════════════════════════════════════ */
  function initFilterDesktopFix() {
    var trigger = document.querySelector('[data-filter-trigger]');
    if (!trigger) return;

    /* ── Add / remove body class (drives CSS tint + panel slide) ── */
    function openFilter() {
      document.body.classList.add('filter-modal-open');
      if (window.__lenis) window.__lenis.stop();
      // Re-init sliders in case AJAX updated the panel while it was closed
      initPriceSliders();
    }

    function closeFilter() {
      document.body.classList.remove('filter-modal-open');
      if (window.__lenis) window.__lenis.start();
    }

    /* ── Watch aria-expanded — facets.js sets this on EVERY open/close ──
       This is the only hook we need. No monkey-patching required.      */
    new MutationObserver(function () {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        openFilter();
      } else {
        closeFilter();
      }
    }).observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });

    /* ── Click-outside close ─────────────────────────────────────────
       Capture phase fires before any child handler, so event
       propagation issues in the panel cannot swallow the click.      */
    document.addEventListener('click', function (e) {
      if (!document.body.classList.contains('filter-modal-open')) return;
      var panel = document.querySelector('[data-filter-panel]');
      if (panel && panel.contains(e.target)) return;  /* inside panel — ignore */
      if (trigger.contains(e.target)) return;          /* on trigger — ignore (its handler toggles) */
      /* Outside — close */
      if (typeof FacetFiltersForm !== 'undefined' &&
          typeof FacetFiltersForm.toggleFilterPanel === 'function') {
        FacetFiltersForm.toggleFilterPanel(false);
      } else {
        closeFilter();
        trigger.setAttribute('aria-expanded', 'false');
      }
    }, true /* useCapture */);
  }

  /* ════════════════════════════════════════════════════════════
     PRODUCT CARD REVEAL — staggered entrance + scroll reveal
     Animates grid cards on initial load and on scroll for
     below-fold cards. Load-more items handled in initLoadMore.
     ════════════════════════════════════════════════════════════ */
  function initProductCardReveal() {
    var scope = document.querySelector('.collection-redesign-scope');
    var grid  = document.getElementById('product-grid');
    if (!grid || typeof gsap === 'undefined') return;

    var cards = Array.from(grid.querySelectorAll('.grid__item'));
    if (!cards.length) return;

    // Mark container so CSS hides items before JS runs
    if (scope) scope.classList.add('pux-animating');

    // Split into above-fold and below-fold
    var vh = window.innerHeight;
    var above = [];
    var below = [];
    cards.forEach(function (c) {
      var top = c.getBoundingClientRect().top;
      if (top < vh + 60) { above.push(c); }
      else               { below.push(c); }
    });

    // Animate title + price together, as one matched reveal, appearing
    // as soon as the card itself is in (small delay just so it reads as a
    // deliberate follow-through rather than instant/jarring).
    function animateCardInfo(card, cardDelay) {
      var title = card.querySelector('.product-card__title');
      var price = card.querySelector('.price');
      var infoDelay = cardDelay + 0.05;
      if (title) {
        gsap.fromTo(title,
          { y: 10, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', delay: infoDelay }
        );
      }
      if (price) {
        gsap.fromTo(price,
          { y: 10, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', delay: infoDelay }
        );
      }
    }

    // Reveal above-fold immediately — noticeable one-by-one stagger
    if (above.length) {
      gsap.fromTo(above,
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0,
          duration: 0.6, ease: 'power3.out',
          stagger: 0.13, delay: 0.06,
          onComplete: function () {
            if (scope) scope.classList.remove('pux-animating');
          }
        }
      );
      // Internal title → price stagger per card
      above.forEach(function (card, i) {
        animateCardInfo(card, 0.06 + i * 0.13);
      });
    } else {
      if (scope) scope.classList.remove('pux-animating');
    }

    // Scroll-triggered reveal for below-fold cards — each card individually
    if (below.length && ScrollTrigger) {
      below.forEach(function (card) {
        gsap.fromTo(card,
          { opacity: 0, y: 24 },
          {
            opacity: 1, y: 0,
            duration: 0.55, ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 95%',
              once: true,
              onEnter: function () { animateCardInfo(card, 0); }
            }
          }
        );
        // Set title + price hidden initially so they can animate in on scroll
        var title = card.querySelector('.product-card__title');
        var price = card.querySelector('.price');
        if (title) gsap.set(title, { opacity: 0, y: 10 });
        if (price) gsap.set(price, { opacity: 0, y: 10 });
      });
    }
  }

  /* ════════════════════════════════════════════════════════════
     FILTER SWIPE-TO-CLOSE — touch gesture (Suggestion 11)
     Mimics iOS's native edge-swipe-back: only arms when the touch
     STARTS within EDGE_ZONE_PX of the left edge, and only ever commits
     to closing if the gesture stays clearly horizontal throughout. This
     is what stops an ordinary vertical scroll through the filter list
     (which naturally has some horizontal finger drift) from being
     misread as a close-swipe and yanking the panel shut mid-scroll.
     Drag right > 80px (from an edge start, staying horizontal) to close.
     ════════════════════════════════════════════════════════════ */
  function initFilterSwipeClose() {
    var panel = document.querySelector('[data-filter-panel]');
    if (!panel) return;

    var EDGE_ZONE_PX = 24;

    var startX   = 0;
    var startY   = 0;
    var currentX = 0;
    var dragging = false;   // touch started at the edge, eligible to become a close-swipe
    var isSwipe  = false;   // gesture has proven itself clearly horizontal

    panel.addEventListener('touchstart', function (e) {
      startX   = e.touches[0].clientX;
      startY   = e.touches[0].clientY;
      currentX = startX;
      dragging = startX <= EDGE_ZONE_PX;
      isSwipe  = false;
      if (dragging) panel.style.transition = 'none';
    }, { passive: true });

    panel.addEventListener('touchmove', function (e) {
      if (!dragging) return;
      currentX = e.touches[0].clientX;
      var dx = currentX - startX;
      var dy = e.touches[0].clientY - startY;

      // Only treat as a swipe once horizontal movement clearly dominates —
      // a vertical scroll through filter options never satisfies this.
      if (!isSwipe) {
        if (Math.abs(dx) > Math.abs(dy) * 2 && Math.abs(dx) > 10) {
          isSwipe = true;
        } else if (Math.abs(dy) > 10) {
          // Vertical scroll confirmed first — stop tracking for this touch.
          dragging = false;
          panel.style.transition = '';
          panel.style.transform  = '';
          return;
        }
      }

      if (isSwipe && dx > 0) {
        panel.style.transform = 'translateX(' + dx + 'px)';
      }
    }, { passive: true });

    panel.addEventListener('touchend', function () {
      if (!dragging) return;
      dragging = false;
      panel.style.transition = '';
      panel.style.transform  = '';

      var dx = currentX - startX;
      if (isSwipe && dx > 80 && typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.toggleFilterPanel(false);
      }
      isSwipe = false;
    });
  }

  /* ════════════════════════════════════════════════════════════
     COLOUR SWATCHES — filters + variant picker (Suggestion 4)
     Scans filter labels and variant option buttons for colour
     names; injects a coloured dot via data-swatch attribute.
     ════════════════════════════════════════════════════════════ */
  var SWATCH_COLOURS = {
    'silver': '#c0c0c0',
    '925':    '#c0c0c0',
    'sterling silver': '#c0c0c0',
    'gold':         '#d4af37',
    '14k':          '#d4af37',
    '14k gold':     '#d4af37',
    '18k':          '#cfb53b',
    '18k gold':     '#cfb53b',
    'yellow gold':  '#d4af37',
    'white gold':   '#e8e4dd',
    'rose gold':    '#b5737d',
    'rosé gold':    '#b5737d',
    'black':        '#2a2828',
    'bronze':       '#cd7f32',
    'copper':       '#b87333',
    'red':          '#c0392b',
    'blue':         '#2980b9',
    'green':        '#27ae60',
    'white':        '#f5f4f2',
    'brown':        '#795548',
  };

  function applySwatchToEl(el) {
    if (el.dataset.swatchApplied) return;
    var text = el.textContent.trim().toLowerCase();
    var hex  = null;
    // Exact match first
    if (SWATCH_COLOURS[text]) {
      hex = SWATCH_COLOURS[text];
    } else {
      // Partial match
      for (var key in SWATCH_COLOURS) {
        if (text.indexOf(key) !== -1) { hex = SWATCH_COLOURS[key]; break; }
      }
    }
    if (!hex) return;
    el.dataset.swatchApplied = '1';
    el.dataset.swatch = '1';
    el.style.setProperty('--sw-color', hex);
  }

  function initColorSwatches() {
    // Filter panel labels
    function applyToFilters() {
      document.querySelectorAll(
        '.facets__item label, .facet-checkbox__label, .facets__label, .facet-option-chip__label'
      ).forEach(applySwatchToEl);
    }
    applyToFilters();

    // Re-apply when facets re-render (AJAX)
    var grid = document.getElementById('product-grid');
    if (grid) {
      new MutationObserver(function () {
        setTimeout(applyToFilters, 100);
      }).observe(grid, { childList: true });
    }

    // Filter panel re-render
    var filterPanel = document.querySelector('[data-filter-panel]');
    if (filterPanel) {
      new MutationObserver(applyToFilters).observe(filterPanel, { childList: true, subtree: true });
    }

    // Variant picker: swatch buttons on product page
    document.querySelectorAll(
      '.product-form__input .product-form__input--swatch .swatch-input + label,' +
      '.product-form__input [data-value],' +
      '.variant-input-wrap label'
    ).forEach(applySwatchToEl);
  }

  /* ════════════════════════════════════════════════════════════
     MATERIAL ACCORDION — product page (Suggestion 6)
     Reads product tags starting with "material:" and builds
     a collapsible breakdown panel below the description.
     ════════════════════════════════════════════════════════════ */
  function initMaterialAccordion() {
    /* Material tags are preserved in the DOM for SEO (Google crawls them)
       but rendered visually hidden — no accordion, no interactive UI.
       To restore the visual accordion, replace this function body with
       the original implementation from git history. */
    var descWrapper = document.querySelector('.product__description-wrapper');
    if (!descWrapper) return;

    var form = document.querySelector('[data-type="add-to-cart-form"]');
    if (!form) return;

    var raw = form.dataset.materials || '';
    if (!raw) return;

    var tags = raw.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    if (!tags.length) return;

    // Output tags as SEO-only hidden list — visible to crawlers, invisible to users
    var wrap = document.createElement('div');
    wrap.className = 'product-materials product-materials--seo-only';
    wrap.setAttribute('aria-hidden', 'true');

    var list = document.createElement('ul');
    list.className = 'visually-hidden'; // base.css defines this
    tags.forEach(function (tag) {
      var li = document.createElement('li');
      li.textContent = tag;
      list.appendChild(li);
    });

    wrap.appendChild(list);
    descWrapper.after(wrap);
    // No click handlers, no accordion JS — purely SEO metadata
  }

  /* ════════════════════════════════════════════════════════════
     SIZE GUIDE DRAWER — replaces modal (Suggestion 7)
     Intercepts any link/button with data-size-guide and opens
     the content in a right-side drawer instead of a popup.
     ════════════════════════════════════════════════════════════ */
  function initSizeGuideDrawer() {
    /* Skip if the Liquid snippet already rendered a proper size-guide drawer —
       prevents duplicate overlay/drawer elements with conflicting state. */
    if (document.querySelector('[data-size-guide-drawer]')) return;

    var triggers = document.querySelectorAll('[data-size-guide], .size-guide-trigger, a[href*="size"]');
    if (!triggers.length) return;

    // Build drawer once
    var overlay = document.createElement('div');
    overlay.className = 'size-guide-overlay';
    document.body.appendChild(overlay);

    var drawer = document.createElement('div');
    drawer.className = 'size-guide-drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('tabindex', '-1');
    drawer.innerHTML =
      '<div class="size-guide-drawer__header">' +
        '<h2 class="size-guide-drawer__title">Size guide</h2>' +
        '<button class="size-guide-drawer__close" aria-label="Close size guide">' +
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6">' +
          '<line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>' +
          '</svg>' +
        '</button>' +
      '</div>' +
      '<div class="size-guide-drawer__body" id="size-guide-content"></div>';
    document.body.appendChild(drawer);

    function open(content) {
      var contentEl = drawer.querySelector('#size-guide-content');
      if (content) contentEl.innerHTML = content;
      drawer.classList.add('is-open');
      overlay.classList.add('is-visible');
      drawer.setAttribute('aria-hidden', 'false');
      if (window.__lenis) window.__lenis.stop();
    }

    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      drawer.setAttribute('aria-hidden', 'true');
      if (window.__lenis) window.__lenis.start();
    }

    drawer.querySelector('.size-guide-drawer__close').addEventListener('click', close);
    overlay.addEventListener('click', close);
    document.addEventListener('keyup', function (e) { if (e.key === 'Escape') close(); });

    triggers.forEach(function (el) {
      // Only intercept if it's specifically a size guide link (not all anchor tags)
      if (el.tagName === 'A' && !el.dataset.sizeGuide) return;
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open('<p style="font-size:14px;line-height:1.7;color:#4a4540;">Size guide content can be configured in your Shopify admin under \'Pages\'. Add a page titled \'Size Guide\' and it will appear here.</p>');
      });
    });
  }

  /* ════════════════════════════════════════════════════════════
     16. LOAD MORE — AJAX infinite-style button
     ════════════════════════════════════════════════════════════ */
  function initLoadMore() {
    /* F6: Use event delegation so load-more survives AJAX DOM replacement from facets */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('#lm-btn');
      if (!btn) return;
      if (btn.classList.contains('is-loading')) return;

      var url = btn.dataset.next;
      if (!url) return;

      var shownEl  = document.getElementById('lm-shown');
      var barEl    = document.getElementById('lm-bar');
      var total    = parseInt(btn.dataset.total || '0', 10);
      var shown    = shownEl ? parseInt(shownEl.textContent, 10) : 0;

      btn.classList.add('is-loading');

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc      = new DOMParser().parseFromString(html, 'text/html');
          var newItems = doc.querySelectorAll('#product-grid .grid__item');
          var grid     = document.getElementById('product-grid');

          newItems.forEach(function (item) {
            item.dataset.lmNew = '1';
            grid.appendChild(item);
          });

          shown += newItems.length;

          if (shownEl) shownEl.textContent = shown;

          if (barEl && total > 0) {
            barEl.style.width = Math.min(Math.round(shown * 100 / total), 100) + '%';
          }

          // Animate new items
          var newEls = Array.from(grid.querySelectorAll('[data-lm-new]'));
          if (typeof gsap !== 'undefined') {
            gsap.fromTo(newEls,
              { opacity: 0, y: 28 },
              { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out', stagger: 0.06 }
            );
            newEls.forEach(function (card, i) {
              var title = card.querySelector('.product-card__title');
              var price = card.querySelector('.price');
              var infoDelay = i * 0.06 + 0.05;
              if (title) gsap.fromTo(title, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', delay: infoDelay });
              if (price) gsap.fromTo(price, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', delay: infoDelay });
            });
          }
          newEls.forEach(function (el) { el.removeAttribute('data-lm-new'); });

          // Freshly-appended cards have no favourite state painted on them yet
          if (window.BabucoFavourites && typeof window.BabucoFavourites.syncAll === 'function') {
            window.BabucoFavourites.syncAll();
          }

          // Lenis resize
          if (window.__lenis) window.__lenis.resize();

          // Grab next page URL from fetched page
          var nextBtn = doc.getElementById('lm-btn');
          if (nextBtn && nextBtn.dataset.next) {
            btn.dataset.next = nextBtn.dataset.next;
            btn.classList.remove('is-loading');
          } else {
            // All products loaded — hide button, fill bar
            if (barEl) barEl.style.width = '100%';
            btn.classList.remove('is-loading');
            btn.style.opacity = '0';
            btn.style.pointerEvents = 'none';
            setTimeout(function () {
              var section = document.getElementById('lm-section');
              if (section) {
                // Just hide the button, keep progress meta
                btn.style.display = 'none';
              }
              // Nothing left to page through — reveal the "design your
              // own" callout at the bottom of the grid.
              var dyoCta = document.getElementById('design-your-own-cta');
              if (dyoCta) dyoCta.hidden = false;
            }, 400);
          }
        })
        .catch(function (err) {
          console.error('[LoadMore]', err);
          btn.classList.remove('is-loading');
        });
    });
  }

  /* ════════════════════════════════════════════════════════════
     18. ANNOUNCEMENT BAR MARQUEE (Suggestion 6)
     Turns the static announcement bar message into a slow
     scrolling marquee without touching Shopify section HTML.
     ════════════════════════════════════════════════════════════ */
  function initAnnouncementMarquee() {
    var bar = document.querySelector('.announcement-bar__message, .announcement-bar .announcement-bar__link');
    if (!bar) return;

    var text = bar.textContent.trim();
    if (!text || text.length < 2) return;

    // Already converted
    if (bar.querySelector('.ann-track')) return;

    var sep     = '  ·  ';
    var content = text + sep;
    var track   = document.createElement('span');
    track.className = 'ann-track';
    track.setAttribute('aria-hidden', 'true');
    track.style.cssText = 'display:inline-block; white-space:nowrap; will-change:transform;';
    track.textContent   = content + content + content + content;

    // Hide original text, replace with track
    bar.style.overflow  = 'hidden';
    bar.style.display   = 'block';
    bar.style.whiteSpace = 'nowrap';
    bar.textContent     = '';
    bar.appendChild(track);

    if (typeof gsap !== 'undefined') {
      gsap.to(track, {
        x: '-50%',
        duration: 28,
        ease: 'none',
        repeat: -1,
      });
      bar.parentElement && bar.parentElement.addEventListener('mouseenter', function () {
        gsap.globalTimeline.timeScale(0.4);
      });
      bar.parentElement && bar.parentElement.addEventListener('mouseleave', function () {
        gsap.globalTimeline.timeScale(1);
      });
    }
  }

  /* ════════════════════════════════════════════════════════════
     SUGGESTION 2 — Collection banner parallax
     Subtle scale(1.06) parallax on the collection hero image.
     ════════════════════════════════════════════════════════════ */
  function initCollectionBannerParallax() {
    if (REDUCED || typeof gsap === 'undefined' || !ScrollTrigger) return;

    var bannerImg = document.querySelector(
      '.collection-hero img, .collection__image-wrapper img, .collection-banner img'
    );
    if (!bannerImg) return;

    var wrapper = bannerImg.closest(
      '.collection-hero, .collection__image-wrapper, .collection-banner'
    );
    if (!wrapper) return;

    // Blur-up: mark wrapper as loaded when image is ready
    function markLoaded() {
      wrapper.classList.add('img-loaded');
    }
    if (bannerImg.complete) {
      markLoaded();
    } else {
      bannerImg.addEventListener('load', markLoaded);
    }

    gsap.fromTo(bannerImg,
      { scale: 1.0 },
      {
        scale: 1.07,
        ease: 'none',
        scrollTrigger: {
          trigger: wrapper,
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        }
      }
    );
  }

  /* ════════════════════════════════════════════════════════════
     SUGGESTION 5 — Product description anchor links
     Adds an in-page # anchor icon next to h2/h3 headings.
     ════════════════════════════════════════════════════════════ */
  function initProductDescAnchors() {
    var desc = document.querySelector('.product__description');
    if (!desc) return;

    desc.querySelectorAll('h2, h3').forEach(function (heading, i) {
      if (heading.querySelector('.desc-anchor')) return; // already done

      var id = heading.id || ('desc-' + i + '-' + heading.textContent.trim()
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
      heading.id = id;

      var link = document.createElement('a');
      link.className = 'desc-anchor';
      link.href = '#' + id;
      link.setAttribute('aria-label', 'Link to ' + heading.textContent.trim());
      link.textContent = '#';
      heading.style.position = 'relative';
      heading.appendChild(link);

      link.addEventListener('click', function (e) {
        e.preventDefault();
        if (window.__lenis) {
          window.__lenis.scrollTo('#' + id, { offset: -100, duration: 1 });
        } else {
          heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ════════════════════════════════════════════════════════════
     I10. INFINITE SCROLL — automatically loads next page when
     the user scrolls within 300px of the Load More button.
     Stored preference: localStorage key 'babuco_infinite_scroll'
     ════════════════════════════════════════════════════════════ */
  function initInfiniteScroll() {
    var STORAGE_KEY = 'babuco_infinite_scroll';
    var toggle = document.getElementById('infinite-scroll-toggle');
    var isEnabled = localStorage.getItem(STORAGE_KEY) === 'true';

    if (toggle) {
      toggle.checked = isEnabled;
      toggle.addEventListener('change', function () {
        isEnabled = toggle.checked;
        localStorage.setItem(STORAGE_KEY, isEnabled);
      });
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!isEnabled || ticking) return;
      requestAnimationFrame(function () {
        var btn = document.getElementById('lm-btn');
        if (!btn || btn.classList.contains('is-loading') || btn.style.display === 'none') {
          ticking = false;
          return;
        }
        var rect = btn.getBoundingClientRect();
        if (rect.top < window.innerHeight + 300) {
          btn.click();
        }
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }

  /* ════════════════════════════════════════════════════════════
     I4. BACK TO TOP BUTTON
     Appears after 400px scroll; smooth-scrolls to top on click.
     ════════════════════════════════════════════════════════════ */
  function initBackToTop() {
    var btn = document.getElementById('btt-btn');
    if (!btn) return;

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          var visible = window.scrollY > 400;
          btn.hidden = !visible;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', function () {
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { duration: 1.2 });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  }

  /* ════════════════════════════════════════════════════════════
     SUGGESTION 1 — Price micro-typography
     Wraps the currency symbol in a smaller styled span.
     ════════════════════════════════════════════════════════════ */
  function initPriceMicroType() {
    document.querySelectorAll('.price .money, .price__regular .money, .price__sale .money').forEach(function (el) {
      if (el.dataset.microDone) return;
      el.dataset.microDone = '1';
      var text = el.textContent.trim();
      // Match leading non-digit characters as currency symbol
      var match = text.match(/^([^\d\s,\.]+)(.*)/);
      if (!match) return;
      el.innerHTML = '<span class="price-currency">' + match[1] + '</span>' + match[2];
    });
  }

})();

